import { useEffect, useState } from 'react'
import { ChevronRight, X, Settings2, RotateCcw } from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { Input } from '@/core/ui/Input'
import {
  errosDaViagem,
  nowHHMM,
  viagemProducao,
  type CarreteiroFichaState,
  type ViagemDraft,
} from './carreteiroStore'
import type { CatLocalDescargaUrb } from '@/core/db/entities'
import { useLocaisDescargaUrb } from '@/core/db/daos'
import { PickerSheet } from '@/features/common/PickerSheet'

/**
 * Modal slim de viagem — caminho rápido usando o "Padrão do dia". Mostra só o
 * que muda viagem a viagem (análise de 3853 fichas — ver carreteiroStore.ts):
 *
 *   - Hora carga / descarga    → obrigatórias, pré-preenchidas com `now`
 *   - Estaca descarga (R)      → obrigatória com destino pista ou estaca origem
 *   - Destino (U)              → troca quando muda (23% das fichas)
 *   - Quantidade               → sempre obrigatória (> 0)
 *   - NF (ficha com NF)        → obrigatória, incremental se numérica
 *
 * "Salvar" só libera sem erros das regras do SIGA. "Mais opções" abre o
 * formulário completo (apontador, dimensões, posição…).
 */

interface Props {
  ficha: CarreteiroFichaState
  draft: ViagemDraft // já criado via newViagemFromDefaults
  onSave: (v: ViagemDraft) => void
  onAdvanced: (v: ViagemDraft) => void // pra "Mais opções"
  onDismiss: () => void
}

export function ViagemQuickSheet({ ficha, draft, onSave, onAdvanced, onDismiss }: Props) {
  const comNf = ficha.fichaConcreto === 'S'
  const [cargaHora, setCargaHora] = useState(draft.cargaHora ?? nowHHMM())
  const [descargaHora, setDescargaHora] = useState(draft.descargaHora ?? nowHHMM())
  const [descargaEstaca, setDescargaEstaca] = useState(draft.descargaEstacaDescarga ?? '')
  const [volume, setVolume] = useState(draft.volume?.toString() ?? '')
  const [nf, setNf] = useState(draft.nf ?? '')
  const [destino, setDestino] = useState({
    id: draft.localDescargaId,
    nome: draft.localDescargaNome,
    deposito: draft.localDescargaDeposito,
  })
  const [pickingDestino, setPickingDestino] = useState(false)

  const locaisUrb = useLocaisDescargaUrb(ficha.projetoId) ?? []

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onDismiss])

  function build(): ViagemDraft {
    return {
      ...draft,
      cargaHora: cargaHora.trim() || null,
      descargaHora: descargaHora.trim() || null,
      descargaEstacaDescarga: ficha.tipo === 'R' ? descargaEstaca.trim() || null : null,
      localDescargaId: destino.id,
      localDescargaNome: destino.nome,
      localDescargaDeposito: destino.deposito,
      volume: parseNum(volume),
      nf: comNf ? nf.trim() || null : null,
    }
  }

  const atual = build()
  // Regras do SIGA + NF obrigatória na ficha com NF (o tíquete manda).
  const pendentes = errosDaViagem(ficha, atual).map((e) => e.mensagem)
  if (comNf && nf.trim() === '') pendentes.push('Preencha a NF / ticket')
  const podeSalvar = pendentes.length === 0

  const previewProducao = viagemProducao(ficha, atual)
  const unidadeLabel = draft.servico.unidadeNome ?? ''
  const estacaObrigatoria =
    ficha.tipo === 'R' && (destino.deposito === 'P' || !!draft.cargaEstacaOrigem)

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center bg-black/60 backdrop-blur-sm"
      onClick={onDismiss}
    >
      <div
        className="card-surface w-full sm:max-w-md max-h-[90vh] rounded-t-2xl sm:rounded-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-center pt-3 pb-1 sm:hidden">
          <div className="h-1 w-12 rounded-full bg-on-surface-muted" />
        </div>

        <div className="flex items-start justify-between px-4 pt-4 pb-3 border-b border-border">
          <div className="flex-1 min-w-0 pr-2">
            <p className="text-xs font-bold text-accent uppercase">Nova viagem · {draft.servico.nome}</p>
            <p className="text-xs text-on-surface-dim mt-1 truncate">
              {draft.localCargaNome ?? '—'} → {destino.nome ?? '—'}
              {draft.servico.materialNome
                ? ` · ${draft.servico.materialNome}${unidadeLabel ? ` (${unidadeLabel})` : ''}`
                : ''}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onDismiss} aria-label="Fechar">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-accent uppercase">Horário</span>
              <button
                type="button"
                onClick={() => {
                  const now = nowHHMM()
                  setCargaHora(now)
                  setDescargaHora(now)
                }}
                className="text-xs text-on-surface-dim hover:text-accent inline-flex items-center gap-1"
              >
                <RotateCcw className="h-3 w-3" />
                Agora
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <LabeledInput label="Carga" value={cargaHora} onChange={setCargaHora} type="time" />
              <LabeledInput label="Descarga" value={descargaHora} onChange={setDescargaHora} type="time" />
            </div>
          </div>

          {ficha.tipo === 'R' && (
            <div>
              <span className="text-xs font-bold text-accent uppercase block mb-2">
                Estaca de descarga{estacaObrigatoria ? '' : ' · opcional'}
              </span>
              <LabeledInput
                label="Estaca"
                value={descargaEstaca}
                onChange={setDescargaEstaca}
                placeholder="ex.: 00132"
                maxLength={6}
                inputMode="numeric"
              />
            </div>
          )}

          {ficha.tipo === 'U' && (
            <div>
              <span className="text-xs font-bold text-accent uppercase block mb-2">Destino</span>
              <PickerField
                label="Local"
                value={destino.nome ?? 'Selecionar…'}
                onClick={() => setPickingDestino(true)}
              />
              {destino.id !== ficha.defaults.localDescargaId && (
                <button
                  type="button"
                  onClick={() =>
                    setDestino({
                      id: ficha.defaults.localDescargaId,
                      nome: ficha.defaults.localDescargaNome,
                      deposito: ficha.defaults.localDescargaDeposito,
                    })
                  }
                  className="mt-1 text-xs text-on-surface-dim hover:text-accent"
                >
                  ↺ usar padrão da ficha
                </button>
              )}
            </div>
          )}

          <div>
            <span className="text-xs font-bold text-accent uppercase block mb-2">
              {comNf ? 'NF & Quantidade' : 'Quantidade'}
            </span>
            <div className={comNf ? 'grid grid-cols-2 gap-3' : ''}>
              {comNf && (
                <LabeledInput
                  label="NF / Ticket"
                  value={nf}
                  onChange={setNf}
                  placeholder="ex.: 12345"
                  maxLength={10}
                />
              )}
              <NumericField label="Quantidade (M³/Ton)" value={volume} onChange={setVolume} />
            </div>
          </div>

          <div className="rounded-lg bg-surface-2/60 px-3 py-2 text-xs">
            <div className="flex justify-between items-baseline">
              <span className="text-on-surface-dim">Produção:</span>
              <span className="text-on-surface font-bold">
                {previewProducao.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                {unidadeLabel ? ` ${unidadeLabel}` : ''}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => onAdvanced(build())}
            className="w-full text-sm text-on-surface-dim hover:text-accent inline-flex items-center justify-center gap-2 py-2"
          >
            <Settings2 className="h-4 w-4" />
            Mais opções (apontador, dimensões…)
          </button>
        </div>

        <div className="border-t border-border px-4 py-3 space-y-2">
          {!podeSalvar && (
            <p className="text-xs text-warn text-center">{Array.from(new Set(pendentes)).join(' · ')}</p>
          )}
          <div className="flex gap-3">
            <Button variant="outline" size="lg" onClick={onDismiss} className="flex-1">
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={() => podeSalvar && onSave(atual)}
              disabled={!podeSalvar}
              className="flex-1"
            >
              Salvar viagem
            </Button>
          </div>
        </div>
      </div>

      {pickingDestino && ficha.tipo === 'U' && (
        <PickerSheet<CatLocalDescargaUrb>
          open
          title="Destino"
          items={locaisUrb}
          label={(l) => l.nome}
          sublabel={(l) => l.bairro ?? null}
          onPick={(l) => {
            setDestino({ id: l.id, nome: l.nome, deposito: l.deposito })
            setPickingDestino(false)
          }}
          onDismiss={() => setPickingDestino(false)}
          searchHint="Buscar destino"
          emptyMessage="Nenhum destino cadastrado"
        />
      )}
    </div>
  )
}

function PickerField({ label, value, onClick }: { label: string; value: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full card-surface-variant text-left px-4 py-3 flex items-center gap-3 min-h-touch hover:bg-surface-2/80 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-on-surface-dim uppercase">{label}</p>
        <p className="text-on-surface mt-0.5 truncate">{value}</p>
      </div>
      <ChevronRight className="h-5 w-5 text-on-surface-dim" />
    </button>
  )
}

function LabeledInput({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  maxLength,
  inputMode,
}: {
  label: string
  value: string
  onChange: (s: string) => void
  placeholder?: string
  type?: string
  maxLength?: number
  inputMode?: 'numeric' | 'text'
}) {
  return (
    <div>
      <label className="text-xs font-medium text-on-surface-dim uppercase">{label}</label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        inputMode={inputMode}
        className="mt-1"
      />
    </div>
  )
}

function NumericField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (s: string) => void
}) {
  return (
    <div>
      <label className="text-xs font-medium text-on-surface-dim uppercase">{label}</label>
      <Input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => {
          const v = e.target.value
          if (v === '' || /^[0-9]*[.,]?[0-9]*$/.test(v)) onChange(v.replace(',', '.'))
        }}
        className="mt-1"
      />
    </div>
  )
}

function parseNum(s: string): number | null {
  if (s.trim() === '') return null
  const n = Number(s.replace(',', '.'))
  return Number.isFinite(n) ? n : null
}
